import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    FormControl,
    FormLabel,
    Autocomplete,
    MenuItem,
    Select,
    Grid2,
    FormControlLabel,
    Radio,
    FormHelperText,
    Divider
} from "@mui/material";

import CommonTextField from "../common/CommonTextField";

const PaymentStepOne = ({ handleNext }) => {
    const [formData, setFormData] = useState({
        school: "",
        email: "",
        address: "",
        city: "",
        state: "",
        cardType: "",
        cardNumber: "",
        cardHolderName: "",
        expiry: "",
        cvv: "",
    });

    const [errors, setErrors] = useState({});
    const [helperText, setHelperText] = useState({});

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateField = (name, value) => {
        let isValid = true;
        let errorMessage = "";

        switch (name) {
            case "school":
            case "address":
            case "city":
            case "state":
            case "cardType":
                if (!value.trim()) {
                    isValid = false;
                    errorMessage = `${name} is required.`;
                }
                break;

            case "email":
                if (!value.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    isValid = false;
                    errorMessage = "Enter a valid email.";
                }
                break;

            case "cardNumber":
                if (!/^\d{16}$/.test(value.replace(/\s/g, ""))) {
                    isValid = false;
                    errorMessage = "Card number must be 16 digits.";
                }
                break;

            case "cardHolderName":
                if (!value.trim()) {
                    isValid = false;
                    errorMessage = "Cardholder name is required.";
                }
                break;

            case "expiry":
                if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) {
                    isValid = false;
                    errorMessage = "Enter a valid expiry date (MM/YY).";
                } else {
                    const [month, year] = value.split("/").map(Number);
                    const now = new Date();
                    const currentYear = parseInt(now.getFullYear().toString().slice(-2), 10);
                    const currentMonth = now.getMonth() + 1;

                    if (year < currentYear || (year === currentYear && month < currentMonth)) {
                        isValid = false;
                        errorMessage = "Expiry date is invalid or in the past.";
                    }
                }
                break;

            case "cvv":
                if (!/^\d{3,4}$/.test(value)) {
                    isValid = false;
                    errorMessage = "CVV must be 3 or 4 digits.";
                }
                break;

            default:
                break;
        }

        setErrors((prev) => ({ ...prev, [name]: !isValid }));
        setHelperText((prev) => ({ ...prev, [name]: errorMessage }));
        return isValid;
    };

    const validateForm = () => {
        const fieldsToValidate = Object.keys(formData);
        let isValid = true;

        fieldsToValidate.forEach((field) => {
            const isFieldValid = validateField(field, formData[field]);
            if (!isFieldValid) isValid = false;
        });

        return isValid;
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (validateForm()) {
            console.log("Form submitted:", formData);
            handleNext();
        }
    };

    const cardTypes = [
        {
            value: "Visa",
            logo: "https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg",
            alt: "Visa",
        },
        {
            value: "MasterCard",
            logo: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
            alt: "MasterCard",
        },
        {
            value: "PayPal",
            logo: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Paypal_Servise.jpg",
            alt: "PayPal",
        },
    ];

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <Grid2 container gap={2} justifyContent={'space-between'}>
                    <Grid2 size={{ md: 12, lg: 5.5 }}>
                        <Typography className="header-label">Billing Details</Typography>

                        <Grid2 container spacing={2}>

                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth error={errors.schoolName}>
                                    <FormLabel className="form-label">School Name</FormLabel>
                                    <CommonTextField
                                        label="Enter School Name"
                                        name="school"
                                        value={formData.school}
                                        onChange={handleInputChange}
                                        error={errors.school}
                                        helperText={helperText.school}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* Email */}
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <FormLabel>Email</FormLabel>
                                    <CommonTextField
                                        label="Enter Email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={errors.email}
                                        helperText={helperText.email}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* Address */}
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <FormLabel>Address</FormLabel>
                                    <CommonTextField
                                        label="Enter Address"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        error={errors.address}
                                        helperText={helperText.address}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* City */}
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <FormLabel>City</FormLabel>
                                    <CommonTextField
                                        label="Enter City"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        error={errors.city}
                                        helperText={helperText.city}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* State */}
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <FormLabel>State</FormLabel>
                                    <Select
                                        name="state"
                                        value={formData.state}
                                        onChange={(e) => handleSelectChange("state", e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select State
                                        </MenuItem>
                                        <MenuItem value="State 1">State 1</MenuItem>
                                        <MenuItem value="State 2">State 2</MenuItem>
                                        <MenuItem value="State 3">State 3</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid2>
                        </Grid2>
                    </Grid2>

                    <Grid2 size={{ md: 12, lg: 6 }}>
                        <Typography className="header-label" gutterBottom>
                            Card Details
                        </Typography>

                        <Grid2 container spacing={2} display={'flex'} justifyContent={'space-between'}>
                            {/* Card Type */}
                            <Grid2 xs={6}>
                                <FormControl fullWidth>
                                    <FormLabel>Card Type</FormLabel>
                                    <Box sx={{
                                        display: "flex", gap: "16px", padding:'16px' , borderRadius: ' 10px',
                                        background: '#F7F7F7 '
                                    }}>
                                        {cardTypes.map((card) => (
                                            <FormControlLabel
                                                key={card.value}
                                                control={
                                                    <Radio
                                                        checked={formData.cardType === card.value}
                                                        onChange={(e) => handleSelectChange("cardType", e.target.value)}
                                                        value={card.value}
                                                        name="cardType"
                                                    />
                                                }
                                                label={
                                                    <Box
                                                        component="img"
                                                        src={card.logo}
                                                        alt={card.alt}
                                                        sx={{
                                                            width: 120,
                                                            height: 50,
                                                            border: formData.cardType === card.value ? "2px solid #1976d2" : "2px solid transparent",
                                                            borderRadius: "8px",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                }
                                            />
                                        ))}

                                    </Box>
                                    <FormHelperText error={errors.cardType}>
                                        {helperText.cardType}
                                    </FormHelperText>
                                </FormControl>
                            </Grid2>

                            {/* Card Number */}
                            <Grid2  size={{ xs: 12,md:6 }}>
                                <FormControl fullWidth>
                                    <FormLabel>Card Number</FormLabel>
                                    <CommonTextField
                                        label="Enter Card Number"
                                        name="cardNumber"
                                        placeholder="XXXX-XXXX-XXXX-XXXX"
                                        value={formData.cardNumber}
                                        onChange={handleInputChange}
                                        error={errors.cardNumber}
                                        helperText={helperText.cardNumber}
                                        sx={{ marginTop: "24px" }}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* Card Holder Name */}
                            <Grid2  size={{ xs: 12,md:6 }}>
                                <FormControl fullWidth>
                                    <FormLabel>Card Holder</FormLabel>
                                    <CommonTextField
                                        label="Enter Card Holder Name"
                                        name="cardHolderName"
                                        value={formData.cardHolderName}
                                        onChange={handleInputChange}
                                        error={errors.cardHolderName}
                                        helperText={helperText.cardHolderName}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* Expiry Date */}
                            <Grid2  size={{ xs: 12,md:6 }}>
                                <FormControl fullWidth>
                                    <FormLabel>Expiry Date (MM/YY)</FormLabel>
                                    <CommonTextField
                                        label="MM/YY"
                                        name="expiry"
                                        placeholder="MM/YY"
                                        value={formData.expiry}
                                        onChange={handleInputChange}
                                        error={errors.expiry}
                                        helperText={helperText.expiry}
                                    />
                                </FormControl>
                            </Grid2>

                            {/* CVV */}
                            <Grid2 size={{ xs: 12,md:6 }}>
                                <FormControl fullWidth>
                                    <FormLabel>CVV</FormLabel>
                                    <CommonTextField
                                        label="Enter CVV"
                                        name="cvv"
                                        type="password"
                                        value={formData.cvv}
                                        onChange={handleInputChange}
                                        error={errors.cvv}
                                        helperText={helperText.cvv}
                                    />
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12 }}>
                                <Box className='payment-box-show' fullWidth>
                                    <Typography fontWeight={700} fontSize={'20px'}>Total Amount</Typography>
                                    <Typography fontWeight={700} fontSize={'20px'}>₹49,999</Typography>
                                </Box>
                            </Grid2>
                            <Grid2 xx={{float:"bottom"}} size={{ xs: 12 }}>
                                <Button  type="submit" className="paymant-submit" fullWidth variant="contained" >
                                    Confirm Payment
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </form>
        </Box>
    );
};

export default PaymentStepOne;
